import React, { memo, useCallback } from 'react';
/** REDUX */
import { useSelector, useDispatch, batch } from 'react-redux';
import {
  setActiveTagAction,
  setMessagesInStoreAction,
  setGlobalLoaderAction,
  setHasMoreMessagesAction,
  scrollToBottomAction,
} from '../../../redux/actions/collaboration';
/** APOLLO */
import { useQuery } from '@apollo/client';
import getTagsQuery from '../apollo/getTagsQuery';
import {
  IGetTagsQuery,
  IGetTagsQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
/** COMPONENTS */
import CollaborationUserList from '../CollaborationUserList';
/** CUSTOM CSS */
import 'react-reflex/styles.css';
/** CUSTOM UI COMPONENTS */
import * as CollaborationMenuUI from './CollaborationMenuUI';
import { workspacePropertySelector } from '../../../redux/selectors/workspace';
import { useAppSelector } from '../../../redux/hooks';

export default memo(function CollaborationMenu() {
  const dispatch = useDispatch();

  const workspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );
  const activeTag = useSelector((state: any) => state.collaboration.activeTag);

  /* After tags are fetched, find the active one and populate the messages in store */
  const onTagChangeCompleted = useCallback(
    ({ workspace }: IGetTagsQuery) => {
      const currentFilterCategory = workspace?.categories?.find(
        (category: any) => category.kind === activeTag,
      );

      const taggedMessages = currentFilterCategory?.tags?.flatMap(
        (tag: any) => {
          let isSelectedTag = false;
          for (const currentTagInfo of tag.message.tags) {
            if (
              currentTagInfo.kind === activeTag &&
              currentTagInfo.state !== 'CLOSED'
            ) {
              isSelectedTag = true;
            }
          }
          return isSelectedTag ? [tag.message] : [];
        },
      );

      batch(() => {
        dispatch(setMessagesInStoreAction(taggedMessages));
        dispatch(setGlobalLoaderAction(false));
        dispatch(setHasMoreMessagesAction(false));
        dispatch(scrollToBottomAction(true));
      });
    },
    [dispatch, activeTag],
  );

  /* Lazy query is not working well with callback
   and because the active tag is not in the variables,
   the most consistent way to fetch the tags on each change is using the refetch */
  const { refetch: fetchTags } = useQuery<
    IGetTagsQuery,
    IGetTagsQueryVariables
  >(getTagsQuery, {
    variables: {
      id: workspaceId,
    },
    skip: !activeTag,
    notifyOnNetworkStatusChange: true,
    onCompleted: onTagChangeCompleted,
  });

  const changeTagFilter = useCallback(
    (tag: string) => {
      if (tag && tag !== activeTag) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchTags();
      }
      batch(() => {
        dispatch(setGlobalLoaderAction(true));
        dispatch(setActiveTagAction(tag === activeTag ? '' : tag));
      });
    },
    [dispatch, activeTag, fetchTags],
  );

  return (
    <CollaborationMenuUI.ContainerLeft>
      <CollaborationMenuUI.StyledReflexContainer orientation="horizontal">
        <CollaborationMenuUI.Users flex={1}>
          <CollaborationUserList />
        </CollaborationMenuUI.Users>
        <CollaborationMenuUI.ResizeHandle />
        <CollaborationMenuUI.UserChatSettingsContainer
          minSize={130}
          maxSize={454}
        >
          <CollaborationMenuUI.UserChatSettings>
            <CollaborationMenuUI.SettingContainer
              onClick={() => changeTagFilter('INSIGHT')}
              isSelected={activeTag === 'INSIGHT'}
            >
              <CollaborationMenuUI.Icon name="lightbulb" />
            </CollaborationMenuUI.SettingContainer>
            <CollaborationMenuUI.SettingContainer
              onClick={() => changeTagFilter('TASK')}
              isSelected={activeTag === 'TASK'}
            >
              <CollaborationMenuUI.Icon name="tasks" />
            </CollaborationMenuUI.SettingContainer>
          </CollaborationMenuUI.UserChatSettings>
        </CollaborationMenuUI.UserChatSettingsContainer>
      </CollaborationMenuUI.StyledReflexContainer>
    </CollaborationMenuUI.ContainerLeft>
  );
});
